import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";

interface MessageLimit {
  monthlyLimit?: number;
  remainingMessages?: number;
  rolloverEmailCredits?: number;
  referralsBoost?: number;
  creditsCycle?: [number, number];
}

interface Props {
  organizationId: number;
  setAlert: SetAlertType;
}

const formatDate = (epoch?: number) =>
  epoch ? new Date(epoch * 1000).toLocaleDateString() : "—";

export default function RefreshEmailLimit({ organizationId, setAlert }: Props) {
  const { getToken } = useAuth();
  const [data, setData] = useState<MessageLimit | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationId}/message-limit`,
        "GET",
      );
      setData(res);
    } catch (error) {
      setAlert(
        error?.message || "An error occurred while loading the email limit",
        "error",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/admin/organizations/${organizationId}/refresh-message-limit`,
        "POST",
      );
      await fetchData();
      setAlert("Email limit cache refreshed", "success");
    } catch (error) {
      setAlert(
        error?.message || "An error occurred while refreshing the email limit",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const rows: [string, string | number][] = [
    ["Limit this cycle", data?.monthlyLimit ?? "—"],
    ["Remaining this cycle", data?.remainingMessages ?? "—"],
    ["Rollover credits", data?.rolloverEmailCredits ?? 0],
    ["Referral bonus", data?.referralsBoost ?? 0],
    [
      "Cycle",
      `${formatDate(data?.creditsCycle?.[0])} – ${formatDate(
        data?.creditsCycle?.[1],
      )}`,
    ],
  ];

  return (
    <>
      <Typography variant="h6">Email Limit</Typography>
      <Grid item>
        <ul>
          {rows.map(([label, value]) => (
            <li key={label}>
              <strong>{label}</strong>: {value}
            </li>
          ))}
        </ul>
      </Grid>
      <LoadingButton
        loading={loading}
        onClick={handleRefresh}
        variant="outlined"
        size="small"
        sx={{ mb: 1 }}
      >
        Refresh email limit cache
      </LoadingButton>
    </>
  );
}
